import { useEffect, useMemo, useState } from 'react';
import { APP_NAME } from '@/lib/brand';
import { Link, useSearchParams } from 'react-router-dom';
import { Bath, BedDouble, Building2, Car, MapPin, Ruler, Search, SlidersHorizontal, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const formatMoney = (price, currency = 'MXN') => new Intl.NumberFormat('es-MX', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price || 0);

function PropertyPortalCard({ item }) {
  const img = item.images?.[0];
  const imgUrl = img ? (img.startsWith('http') ? img : `${API}${img}`) : null;
  const logoUrl = item.company?.logo_url ? (item.company.logo_url.startsWith('http') ? item.company.logo_url : `${API}${item.company.logo_url}`) : null;

  return (
    <Link to={`/propiedad/${item.company?.slug}/${item.id}`} className="block" data-testid={`marketplace-card-${item.id}`}>
      <Card className="overflow-hidden border-stone-200 bg-white hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
        <div className="aspect-[16/11] bg-stone-100 overflow-hidden relative">
          {imgUrl ? <img src={imgUrl} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-10 h-10 text-stone-300" /></div>}
          <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-stone-950/90 via-stone-950/35 to-transparent">
            <div className="flex items-center justify-between gap-3">
              <Badge className="rounded-sm bg-white/90 text-stone-900 border-0 uppercase tracking-[0.18em] text-[10px]">{item.operation_type}</Badge>
              {(item.is_sponsored_public || item.is_featured_public) && <Badge className="rounded-sm bg-amber-200 text-stone-900 border-0 uppercase tracking-[0.18em] text-[10px]">{item.is_sponsored_public ? 'Patrocinada' : 'Destacada'}</Badge>}
            </div>
          </div>
        </div>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">{item.company?.name}</p>
              <h2 className="text-xl font-semibold text-stone-900 leading-tight line-clamp-2">{item.title}</h2>
            </div>
            {logoUrl && <img src={logoUrl} alt={item.company?.name} className="w-12 h-12 object-contain rounded-md border border-stone-200 bg-white p-1" />}
          </div>
          <p className="text-2xl font-bold text-stone-900">{formatMoney(item.price, item.currency)}</p>
          <div className="flex items-center gap-2 text-sm text-stone-500"><MapPin className="w-4 h-4" />{item.zone || item.city || item.state || 'Ubicación disponible'}</div>
          <div className="flex gap-4 text-sm text-stone-600 flex-wrap border-t border-stone-200 pt-4">
            {item.bedrooms > 0 && <span className="flex items-center gap-1"><BedDouble className="w-4 h-4" />{item.bedrooms}</span>}
            {item.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="w-4 h-4" />{item.bathrooms}</span>}
            {item.parking_spots > 0 && <span className="flex items-center gap-1"><Car className="w-4 h-4" />{item.parking_spots}</span>}
            {item.area_m2 > 0 && <span className="flex items-center gap-1"><Ruler className="w-4 h-4" />{item.area_m2} m²</span>}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function DevelopmentPortalCard({ development }) {
  const logoUrl = development.company?.logo_url ? (development.company.logo_url.startsWith('http') ? development.company.logo_url : `${API}${development.company.logo_url}`) : null;
  return (
    <Link to={`/desarrollo/${development.company?.slug}/${development.id}`} className="block" data-testid={`marketplace-development-${development.id}`}>
      <Card className="border-stone-200 bg-stone-950 text-stone-50 hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400">Desarrollo</p>
              <h3 className="text-2xl font-semibold leading-tight">{development.name}</h3>
            </div>
            {logoUrl && <img src={logoUrl} alt={development.company?.name} className="w-12 h-12 rounded-md bg-white object-contain p-1" />}
          </div>
          <p className="text-sm text-stone-300">{development.company?.name}</p>
          <p className="text-xl font-bold text-emerald-300">Desde {formatMoney(development.base_price, development.currency)}</p>
          <p className="text-sm text-stone-400 flex items-center gap-2"><MapPin className="w-4 h-4" />{development.city || development.state || 'Ubicación disponible'}</p>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function MarketplacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [developments, setDevelopments] = useState([]);
  const [facets, setFacets] = useState({ cities: [], zones: [], property_types: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState({
    operation: searchParams.get('operation_type') || '',
    propertyType: searchParams.get('property_type') || '',
    city: searchParams.get('city') || '',
    zone: searchParams.get('zone') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    bathrooms: searchParams.get('bathrooms') || '',
  });

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (filters.operation) params.set('operation_type', filters.operation);
    if (filters.propertyType) params.set('property_type', filters.propertyType);
    if (filters.city) params.set('city', filters.city);
    if (filters.zone) params.set('zone', filters.zone);
    if (filters.bedrooms) params.set('bedrooms', filters.bedrooms);
    if (filters.bathrooms) params.set('bathrooms', filters.bathrooms);
    setSearchParams(params, { replace: true });
    setLoading(true);
    fetch(`${API}/api/marketplace/properties?${params.toString()}`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setDevelopments(d.developments || []);
        setFacets(d.facets || { cities: [], zones: [], property_types: [] });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [query, filters, setSearchParams]);

  const operations = useMemo(() => [...new Set(items.map(item => item.operation_type).filter(Boolean))], [items]);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="marketplace-page">
      <div className="max-w-screen-2xl mx-auto px-5 lg:px-10 py-8 space-y-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-b border-stone-200 pb-8">
          <div className="max-w-3xl space-y-4">
            <Badge className="rounded-sm bg-stone-900 text-white border-0 uppercase tracking-[0.24em] text-[10px]">propiedades.dealider.com</Badge>
            <h1 className="text-4xl lg:text-6xl font-medium tracking-tight leading-none">Encuentra propiedades y desarrollos con una experiencia premium.</h1>
            <p className="text-base text-stone-600 max-w-2xl">Explora el catálogo inmobiliario de {APP_NAME} con fichas enriquecidas, branding real y asistencia inteligente.</p>
          </div>
          <div className="w-full lg:w-[420px] relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Busca por propiedad, ciudad o zona" className="pl-10 h-12 rounded-sm border-stone-300 bg-white shadow-none" data-testid="marketplace-search" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 items-start">
          <aside className="lg:sticky lg:top-6 space-y-6" data-testid="marketplace-sidebar-filters">
            <Card className="border-stone-200 bg-white shadow-none">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center justify-between"><h2 className="text-lg font-semibold">Filtros</h2><SlidersHorizontal className="w-4 h-4 text-stone-500" /></div>
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Operación</p>
                  <div className="flex flex-wrap gap-2">{['', ...operations].map(op => <Button key={op || 'all'} size="sm" variant={filters.operation === op ? 'default' : 'outline'} onClick={() => setFilters(f => ({ ...f, operation: op }))} data-testid={`marketplace-operation-${op || 'all'}`}>{op || 'Todas'}</Button>)}</div>
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Tipo de propiedad</p>
                  <div className="flex flex-wrap gap-2">{['', ...(facets.property_types || [])].map(type => <Button key={type || 'all'} size="sm" variant={filters.propertyType === type ? 'default' : 'outline'} onClick={() => setFilters(f => ({ ...f, propertyType: type }))}>{type || 'Todos'}</Button>)}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))} placeholder="Ciudad" className="border-stone-300 bg-stone-50" data-testid="marketplace-city-filter" />
                  <Input value={filters.zone} onChange={e => setFilters(f => ({ ...f, zone: e.target.value }))} placeholder="Zona" className="border-stone-300 bg-stone-50" data-testid="marketplace-zone-filter" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={filters.bedrooms} onChange={e => setFilters(f => ({ ...f, bedrooms: e.target.value }))} placeholder="Recámaras" className="border-stone-300 bg-stone-50" data-testid="marketplace-beds-filter" />
                  <Input value={filters.bathrooms} onChange={e => setFilters(f => ({ ...f, bathrooms: e.target.value }))} placeholder="Baños" className="border-stone-300 bg-stone-50" data-testid="marketplace-baths-filter" />
                </div>
                <Button variant="outline" className="w-full" onClick={() => { setQuery(''); setFilters({ operation: '', propertyType: '', city: '', zone: '', bedrooms: '', bathrooms: '' }); }} data-testid="marketplace-reset-filters">Limpiar filtros</Button>
              </CardContent>
            </Card>
          </aside>

          <div className="space-y-10">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Resultados</p>
                <h2 className="text-2xl font-semibold">{loading ? 'Cargando propiedades...' : `${items.length} propiedades encontradas`}</h2>
              </div>
            </div>

            {loading ? (
              <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-stone-900" /></div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8" data-testid="marketplace-grid">
                {items.map(item => <PropertyPortalCard key={item.id} item={item} />)}
              </div>
            )}

            {developments.length > 0 && (
              <section className="space-y-5" data-testid="marketplace-developments-section">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Desarrollos</p>
                    <h2 className="text-3xl font-semibold">Desarrollos destacados</h2>
                  </div>
                  <p className="text-sm text-stone-500 max-w-xl text-right">Proyectos activos de desarrolladores y franquicias con disponibilidad pública.</p>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {developments.map(dev => <DevelopmentPortalCard key={dev.id} development={dev} />)}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}